import { ApolloQueryResult } from '@apollo/client';
import { CopyHelper } from 'global/utils/CopyHelper';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { Cart } from 'types/cartService';
import { Config, DomainProductLine, InputFilter } from 'types/domainService';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStore';
import { CartBrowsingCriteria, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { INITIAL_DOMAIN_CONFIGURATION } from 'store/domain/domainStore';

export class RateSegmentsStore {
  rootStore: RootStore;
  loading = false;
  productLines: DomainProductLine[] = [];
  cart: Cart = INITIAL_CART_OBJECT;
  showProductDetailsModal = false;
  isResetModalOpen = false;
  currentEffectiveDate = '';
  currentZipCode = '';
  currentRateSegment: RateSegment = 'GROUP';
  domainConfig: Config = INITIAL_DOMAIN_CONFIGURATION;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setProductLines(productLines: DomainProductLine[]): void {
    this.productLines = productLines;
  }

  private setDomainConfig(config: Config): void {
    this.domainConfig = config;
  }

  private setCurrentEffectiveDate(effectiveDate: string): void {
    this.currentEffectiveDate = effectiveDate;
  }

  private setCurrentZipCode(zipCode: string): void {
    this.currentZipCode = zipCode;
  }

  public setShowProductDetailsModal(value: boolean): void {
    this.showProductDetailsModal = value;
  }

  public setShowResetModal(value: boolean): void {
    this.isResetModalOpen = value;
  }

  public setCurrentRateSegment(rateSegment: RateSegment): void {
    this.currentRateSegment = rateSegment;
  }

  public async collectInitialData(cartId: string): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.cartStore.getCart(cartId);
    void runInAction(async () => {
      const cartData = CopyHelper.deepCopy((res as unknown as ApolloQueryResult<{ getCart: Cart }>).data?.getCart);
      this.cart = cartData;
      const { effectiveDate, zipCode, state, county } = cartData.lastUpdateBrowsingCriteria;

      this.setCurrentEffectiveDate(effectiveDate);
      this.setCurrentZipCode(zipCode);
      const sponsorId = this.rootStore.domainStore.domainHeader.id;

      const inputFilter: InputFilter = {
        effectiveDate,
        zipCode,
        state,
        county,
        agencyId: '',
        sponsorId,
      };

      await Promise.all([this.getProductLines(inputFilter), this.getConfig(sponsorId, effectiveDate)]);
      runInAction(() => {
        // Show a link to the shopping cart in the header and provide the necessary data for redirection
        this.rootStore.domainStore.setDataForCartRedirect(cartId, cartData.groupId, true);
        this.rootStore.domainStore.changeCartItemsQuantity(cartData.cartItems.length);
        this.setLoading(false);
      });
    });
  }

  public async getProductLines(inputFilter: InputFilter): Promise<void> {
    const productLines = await this.rootStore.domainStore.getDomainProductLine(inputFilter);
    this.setProductLines(productLines);
  }

  public async getConfig(sponsorId: string, effectiveDate: string): Promise<void> {
    const config = await this.rootStore.domainStore.getConfigByDate(sponsorId, effectiveDate);
    this.setDomainConfig(config);
  }

  public async resetProgress(): Promise<Cart | undefined> {
    this.setLoading(true);
    const { effectiveDate, zipCode, state, county } = this.cart.lastUpdateBrowsingCriteria;
    const createdCart = (
      await this.rootStore.cartStore.createCart(
        this.cart.groupId,
        'ESTORE',
        // Set the CartBrowsingCriteria type to use the default browsing criteria that will be set in BE.
        {
          effectiveDate,
          zipCode,
          state,
          county,
        } as CartBrowsingCriteria,
        this.rootStore.domainStore.domainHeader.id,
        this.currentRateSegment,
      )
    ).data?.createCart;
    return runInAction((): Cart | undefined => {
      if (createdCart?.id) {
        this.setLoading(false);
        this.cart = createdCart;
        // Show a link to the shopping cart in the header and provide the necessary data for redirection
        this.rootStore.domainStore.setDataForCartRedirect(createdCart.id, createdCart.groupId, true);
        this.rootStore.domainStore.changeCartItemsQuantity(0);
        this.setShowResetModal(false);
        return createdCart;
      }
      return undefined;
    });
  }

  public async updateRateSegment(cartId: string, rateSegment: RateSegment): Promise<void> {
    const updatedCart = await this.rootStore.cartStore.updateRateSegment(cartId, rateSegment);
    runInAction(() => {
      if (updatedCart?.data?.updateRateSegment) {
        this.cart = updatedCart.data.updateRateSegment;
        this.setCurrentRateSegment(updatedCart.data.updateRateSegment.rateSegment);
      }
    });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
